import React, { useState } from 'react';
import { AUTH_ENDPOINTS, FETCH_OPTIONS } from '../../config/constants';
import { useUser } from '../../context/usercontext';

type AlertType = {
  message: string;
  type: 'error' | 'success';
} | null;

interface FirstLoginPasswordProps {
  onSuccess: (role: 'admin' | 'user' | 'root') => void;
}

const FirstLoginPassword: React.FC<FirstLoginPasswordProps> = ({ onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<AlertType>(null);
  const { user } = useUser();

  const showAlert = (message: string, type: 'error' | 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      showAlert('Compila tutti i campi', 'error');
      return;
    }

    if (newPassword.length < 6) {
      showAlert('La password deve essere di almeno 6 caratteri', 'error');
      return;
    }

    // Verifica che contenga almeno una maiuscola
    if (!/[A-Z]/.test(newPassword)) {
      showAlert('La password deve contenere almeno una lettera maiuscola', 'error');
      return;
    }

    // Verifica che contenga almeno un numero
    if (!/[0-9]/.test(newPassword)) {
      showAlert('La password deve contenere almeno un numero', 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlert('Le password non coincidono', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const res = await fetch(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
        method: 'POST',
        ...FETCH_OPTIONS,
        body: JSON.stringify({
          email: user?.email,
          password: newPassword
        })
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg = data?.detail || data?.message || `Errore ${res.status}`;
        showAlert(msg, 'error');
        setLoading(false);
        return;
      }
      
      // Determina il ruolo dalla risposta
      const role = data?.role === 'root' ? 'root' : (data?.role === 'admin' ? 'admin' : 'user');
      
      showAlert('Password cambiata con successo!', 'success');
      setTimeout(() => {
        onSuccess(role);
      }, 1500);
    } catch (error) {
      showAlert(`Errore di connessione: ${error instanceof Error ? error.message : 'Impossibile contattare il server'}`, 'error');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-10 text-center text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <h1 className="text-3xl font-bold mb-2 flex items-center justify-center gap-2">
              <span>🔐</span> Primo Accesso
            </h1>
            <p className="opacity-90">
              Imposta la tua nuova password
            </p>
          </div>

          <div className="p-8">
            {alert && (
              <div className={`p-3 rounded-lg mb-5 flex items-center gap-2 ${alert.type === 'error' ? 'bg-red-50 border border-red-200 text-red-600' : 'bg-green-50 border border-green-200 text-green-600'}`}>
                <span>{alert.type === 'error' ? '❌' : '✅'}</span>
                <span>{alert.message}</span>
              </div>
            )}

            {user && (
              <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                👤 Benvenuto, <strong>{user.nome} {user.cognome}</strong>!<br />
                Per iniziare, imposta una nuova password.
              </div>
            )}

            <div>
              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">🔒 Nuova Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 6 caratteri, 1 maiuscola, 1 numero"
                    className="w-full p-3 pl-12 pr-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                  <span 
                    className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-purple-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️‍🗨️' : '👁️'}
                  </span>
                </div>
              </div>

              <div className="mb-5">
                <label className="block mb-2 font-medium text-gray-600 text-sm">🔒 Conferma Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ripeti la password"
                    className="w-full p-3 pl-12 pr-12 border-2 border-gray-200 rounded-lg bg-gray-50 focus:border-purple-500 focus:bg-white focus:outline-none transition-all"
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                  <span 
                    className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-purple-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
                  </span>
                </div>
              </div>

              <button
                onClick={handleChangePassword}
                disabled={loading}
                className="w-full p-4 rounded-lg text-white font-semibold transition-all hover:shadow-lg disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span> Salvataggio...
                  </span>
                ) : (
                  '💾 Cambia Password'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-5 text-white text-xs opacity-80">
          © 2024 Timesheet Online - Tutti i diritti riservati
        </div>
      </div>
    </div>
  );
};

export default FirstLoginPassword;
